import { ProcessStep, PricingPlan, FAQItem, CaseStudy, Testimonial } from './types';

export const PROCESS_STEPS: ProcessStep[] = [
    { id: 1, title: "Enquiry", description: "Let us know that you're interested via our contact form." },
    { id: 2, title: "Discovery", description: "Our team members will take you through what's involved." },
    { id: 3, title: "Consultation", description: "Discuss your situation, goals and expectations with a surgeon." },
    { id: 4, title: "Support", description: "Our team will help arrange transport, visas (if needed) and airline support." },
    { id: 5, title: "Check-in", description: "Get settled in your accommodation." },
    { id: 6, title: "Pre-surgical Checks", description: "Final checks to ensure you're in good shape for the surgery." },
    { id: 7, title: "Surgery", description: "Complete your surgery." },
    { id: 8, title: "Lengthening", description: "Begin the lengthening process." },
    { id: 9, title: "Rehabilitation", description: "Physiotherapy alongside lengthening ensures you preserve range of motion and function." },
    { id: 10, title: "Follow-ups", description: "Regular check-ins with your surgeon." },
    { id: 11, title: "Success!", description: "Remove your hardware and enjoy!" },
];

export const PRICING_PLANS: PricingPlan[] = [
    {
        id: 'femur',
        title: 'Femur Lengthening',
        price: 'From $35k',
        description: 'Lengthening up to 8cm can be comfortably achieved.',
        image: '/femurs.webp',
    },
    {
        id: 'tibia',
        title: 'Tibia Lengthening',
        price: 'From $37.5k',
        description: 'Lengthening up to 6cm can be comfortably achieved.',
        image: '/tibias.webp',
    },
    {
        id: 'quad',
        title: 'Quadrilateral Tibia & Femur Lengthening',
        price: '',
        description: 'Quadrilateral lengthening can achieve similar gains in a shorter period of time, and help to achieve proportionality. Careful planning and staged or combined procedures, rigorous preoperative assessment and intensive specialist rehabilitation can support good outcomes.',
        image: '/femur & tibias.webp',
    },
];

export const PACKAGE_INCLUDES = [
    "Bilateral femur or tibia lengthening",
    "Hospital Costs",
    "Anesthesia Team",
    "Pain Management Team",
    "Lengthening surgery",
    "Distal IT band release",
    "Hardware",
    "3 months’ accommodation",
    "3 months Paley-trained physiotherapy team",
    "3 months’ clinical follow-up",
    "Airport Transfers",
    "Appointment Transfers",
    "Recovery Buddy",
    "Pathology",
    "Radiology",
    "Clinical Management"
];

export const FAQS: FAQItem[] = [
    { 
        question: "Is stature lengthening safe?", 
        answer: "No surgery is absolutely safe, and if someone tells you differently, don’t believe them! In fact, every year, thousands of patients worldwide are left with deformities because they’ve received poor patient care! That’s why our team delivers the experience, know-how and precision needed to optimise your stature lengthening surgery, ensuring the very highest standard of care, quality and patient safety, and helping you to achieve the best possible outcome." 
    },
    { 
        question: "Is stature lengthening painful?", 
        answer: "Some level of post-operative pain or discomfort is to be expected for around 5-7 days after your surgery, although it will be managed with medication by your care team and will normally begin to decrease with time. The level of pain experienced will vary between patients due to clinical factors and individual pain thresholds. Physiotherapy and stretching exercises can help to reduce any pain you may feel." 
    },
    { 
        question: "Is stature lengthening expensive?", 
        answer: "At Go Lengthening, we want to increase access to high-quality care and help our patients to avoid the painful and debilitating complications that are common when lengthening is performed by inexperienced or unscrupulous surgeons. We honour that commitment by making our services among the most affordable anyway, with stature lengthening packages starting from only $35,000 USD." 
    },
    { 
        question: "How long will stature lengthening take?", 
        answer: "Stature lengthening surgery itself can take around 3 hours. The lengthening phase normally begins around 7 days later and lasts around 90 days depending on your target length. You will be expected to stay in Istanbul during this period. Once this target is reached, it can take around 6 months for the bone to consolidate, with complete recovery by 9-12 months." 
    },
    { 
        question: "What if I don’t want anyone to know about my stature lengthening?", 
        answer: "Elective stature lengthening is an increasingly common procedure that can provide significant benefits for people suffering from body image issues or height dysphoria (where other treatments have failed). However, for patients who prefer to keep their decision private, we can guarantee confidentiality and absolute discretion throughout your time with us." 
    },
];

export const CASE_STUDIES: CaseStudy[] = [
    { 
        id: 1, 
        image: "/leg-1.jpeg", 
        sex: "Female",
        age: "16",
        condition: "4cm leg length discrepancy with hip obliquity and associated scoliosis.",
        solution: "8cm limb lengthening with magnetic intramedullary nail, achieving limb length equality, hip tilt correction and improved spinal alignment."
    },
    { 
        id: 2, 
        image: "/leg-2.jpeg", 
        sex: "Male",
        age: "24",
        condition: "9cm leg length discrepancy following multiple open fractures and infection following car accident.",
        solution: "8cm limb lengthening with magnetic intramedullary nail, achieving good callus formation and function at 12-month follow-up."
    },
    { 
        id: 3, 
        image: "/leg-3.jpeg", 
        sex: "Male",
        age: "17",
        condition: "Fibular Hemimelia.",
        solution: "7cm limb lengthening over three months, achieving correction of pelvic obliquity and excellent regenerate bone quality."
    },
    { 
        id: 4, 
        image: "/leg-4.jpeg",
        sex: "Male",
        age: "20",
        condition: "Congenital femoral length discrepancy (6cm) with condyle hypoplasia and valgus knee deformity.",
        solution: "femoral lengthening with magnetic intramedullary nail (retrograde insertion) for simultaneous lengthening and deformity correction, achieving leg length equality and alignment at six months."
    },
    { 
        id: 5, 
        image: "/leg-5.jpeg", 
        sex: "Female",
        age: "16",
        condition: "Blount’s Disease (Tibia Vara).",
        solution: "5cm bilateral tibial lengthening over five months."
    },
    { 
        id: 6, 
        image: "/leg-6.jpeg", 
        sex: "Female",
        age: "14",
        condition: "Achondroplasia",
        solution: "Bilateral femoral lengthening over five months, achieving good callus formation."
    },
    { 
        id: 7, 
        image: "/leg-7.jpeg", 
        sex: "Male",
        age: "17",
        condition: "Achondroplasia.",
        solution: "Left humeral lengthening using a magnetic intramedullary nail, achieving good alignment and regenerate bone formation."
    },
    { 
        id: 8, 
        image: "/leg-8.jpeg", 
        sex: "Male",
        age: "13",
        condition: "Multiple hereditary exostoses.",
        solution: "5cm femoral lengthening using a magnetic intramedullary nail (retrograde insertion) with deformity correction after four months"
    },
];

export const TESTIMONIALS: Testimonial[] = [
    { id: 1, name: "Sarah J.", role: "Patient", quote: "The support team was incredible. My 'Recovery Buddy' made Istanbul feel like home." },
    { id: 2, name: "Michael R.", role: "Patient", quote: "Professionalism from day one. The rehab facility is better than most 5-star hotels." },
    { id: 3, name: "David K.", role: "Patient", quote: "I gained 7cm and my confidence is through the roof. GoLengthening changed my life." },
    { id: 4, name: "James L.", role: "Patient", quote: "The transparency regarding pricing and the process was refreshing. No hidden costs." },
];